import React, { useState } from 'react';
import {
  Package,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Search,
  Filter,
  Download,
  RefreshCw,
  Plus,
  Eye,
  Edit,
  ArrowRightLeft,
  ShoppingCart,
  Truck,
  Clock,
  CheckCircle,
  XCircle,
  BarChart3,
  Activity,
  Target,
  Zap,
  Building,
  Users,
  Calendar,
  DollarSign
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { comprehensiveMedicationDatabase, ComprehensiveMedication } from '../../data/comprehensiveMedicationDatabase';
import { format, addDays, subDays } from 'date-fns';
import StatCard from '../../components/StatCard';

interface StockItem extends ComprehensiveMedication {
  currentStock: number;
  reservedStock: number;
  availableStock: number;
  reorderPoint: number;
  maximumStock: number;
  lastRestockDate: string;
  nextDeliveryDate?: string;
  stockTurnover: number;
  daysOfStock: number;
  stockStatus: 'critical' | 'low' | 'adequate' | 'overstocked';
  stockValue: number;
  lastMovements: StockMovement[];
  supplier: string;
  leadTimeDays: number;
  averageDailyUsage: number;
}

interface StockMovement {
  id: string;
  type: 'inbound' | 'outbound' | 'adjustment' | 'transfer' | 'expired' | 'damaged';
  quantity: number;
  reason: string;
  date: string;
  reference?: string;
  user: string;
  location?: string;
}

interface StockAlert {
  id: string;
  medicationId: string;
  medicationName: string;
  type: 'critical_low' | 'reorder_point' | 'overstock' | 'no_movement' | 'expiry_soon';
  severity: 'critical' | 'high' | 'medium' | 'low';
  message: string;
  currentStock: number;
  recommendedAction: string;
  daysUntilStockout?: number;
  estimatedLoss?: number;
}

const StockLevels: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedSupplier, setSelectedSupplier] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('stockStatus');
  const [viewMode, setViewMode] = useState<'table' | 'cards' | 'analytics'>('table');
  const [showAlerts, setShowAlerts] = useState(true);

  // Generate comprehensive stock data from medication database
  const stockItems: StockItem[] = comprehensiveMedicationDatabase.map((med, index) => {
    const currentStock = Math.floor(Math.random() * med.maximumStock);
    const reservedStock = Math.floor(currentStock * 0.1);
    const reorderPoint = Math.floor(med.maximumStock * 0.2);
    const averageDailyUsage = med.monthlyVolume / 30;
    const daysOfStock = currentStock / averageDailyUsage;
    
    let stockStatus: 'critical' | 'low' | 'adequate' | 'overstocked';
    if (currentStock <= reorderPoint * 0.5) stockStatus = 'critical';
    else if (currentStock <= reorderPoint) stockStatus = 'low';
    else if (currentStock >= med.maximumStock * 0.9) stockStatus = 'overstocked';
    else stockStatus = 'adequate';

    return {
      ...med,
      currentStock,
      reservedStock,
      availableStock: currentStock - reservedStock,
      reorderPoint,
      maximumStock: med.maximumStock,
      lastRestockDate: format(subDays(new Date(), Math.floor(Math.random() * 30)), 'yyyy-MM-dd'),
      nextDeliveryDate: stockStatus === 'critical' || stockStatus === 'low' ? 
        format(addDays(new Date(), Math.floor(Math.random() * 7) + 1), 'yyyy-MM-dd') : undefined,
      stockTurnover: Math.round((med.monthlyVolume * 12 / currentStock) * 10) / 10,
      daysOfStock: Math.round(daysOfStock),
      stockStatus,
      stockValue: currentStock * med.costOfGoodsSold,
      averageDailyUsage: Math.round(averageDailyUsage * 10) / 10,
      leadTimeDays: Math.floor(Math.random() * 7) + 1,
      supplier: ['Sigma Healthcare', 'API', 'Symbion', 'EBOS Group', 'Pharma Direct'][Math.floor(Math.random() * 5)],
      lastMovements: [
        {
          id: `mov-${index}-1`,
          type: 'outbound',
          quantity: -Math.floor(Math.random() * 20) - 5,
          reason: 'Dispensed to patient',
          date: format(subDays(new Date(), 1), 'yyyy-MM-dd HH:mm'),
          user: 'Dr. Sarah Johnson'
        },
        {
          id: `mov-${index}-2`,
          type: 'inbound',
          quantity: Math.floor(Math.random() * 100) + 50,
          reason: 'Stock delivery',
          date: format(subDays(new Date(), 5), 'yyyy-MM-dd HH:mm'),
          reference: `PO-${Math.floor(Math.random() * 10000)}`,
          user: 'System'
        }
      ]
    };
  });

  // Generate stock alerts
  const stockAlerts: StockAlert[] = stockItems
    .filter(item => item.stockStatus === 'critical' || item.stockStatus === 'low' || item.daysOfStock <= 7)
    .map(item => ({
      id: `alert-${item.id}`,
      medicationId: item.id,
      medicationName: item.name,
      type: item.stockStatus === 'critical' ? 'critical_low' : 
            item.currentStock <= item.reorderPoint ? 'reorder_point' : 'expiry_soon',
      severity: item.stockStatus === 'critical' ? 'critical' : 'high',
      message: item.stockStatus === 'critical' 
        ? `Critical stock level: Only ${item.currentStock} units remaining`
        : `Stock below reorder point: ${item.currentStock} units (reorder at ${item.reorderPoint})`,
      currentStock: item.currentStock,
      recommendedAction: `Order ${item.maximumStock - item.currentStock} units from ${item.supplier}`,
      daysUntilStockout: item.daysOfStock,
      estimatedLoss: item.daysOfStock <= 3 ? (item.averageDailyUsage * 7 * item.retailPrice) : undefined
    }));

  const filteredItems = stockItems
    .filter(item => {
      const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           item.genericName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           item.supplier.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
      const matchesStatus = selectedStatus === 'all' || item.stockStatus === selectedStatus;
      const matchesSupplier = selectedSupplier === 'all' || item.supplier === selectedSupplier;
      
      return matchesSearch && matchesCategory && matchesStatus && matchesSupplier;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'stockStatus':
          const statusOrder = { critical: 0, low: 1, adequate: 2, overstocked: 3 };
          return statusOrder[a.stockStatus] - statusOrder[b.stockStatus];
        case 'daysOfStock':
          return a.daysOfStock - b.daysOfStock;
        case 'stockValue':
          return b.stockValue - a.stockValue;
        case 'name':
          return a.name.localeCompare(b.name);
        default:
          return 0;
      }
    });

  const categories = stockItems.reduce((acc: string[], item) => {
    if (!acc.includes(item.category)) {
      acc.push(item.category);
    }
    return acc;
  }, []);
  const suppliers = stockItems.reduce((acc: string[], item) => {
    if (!acc.includes(item.supplier)) {
      acc.push(item.supplier);
    }
    return acc;
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'low': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'adequate': return 'bg-green-100 text-green-800 border-green-200';
      case 'overstocked': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'critical': return <XCircle className="h-4 w-4 text-red-600" />;
      case 'low': return <AlertTriangle className="h-4 w-4 text-orange-600" />;
      case 'adequate': return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'overstocked': return <TrendingUp className="h-4 w-4 text-blue-600" />;
      default: return <Package className="h-4 w-4 text-gray-600" />;
    }
  };

  // Analytics data
  const stockStatusData = [
    { status: 'Critical', count: stockItems.filter(i => i.stockStatus === 'critical').length, color: '#EF4444' },
    { status: 'Low', count: stockItems.filter(i => i.stockStatus === 'low').length, color: '#F97316' },
    { status: 'Adequate', count: stockItems.filter(i => i.stockStatus === 'adequate').length, color: '#10B981' },
    { status: 'Overstocked', count: stockItems.filter(i => i.stockStatus === 'overstocked').length, color: '#3B82F6' }
  ];

  const stockValueData = categories.map(category => ({
    category,
    value: stockItems.filter(i => i.category === category).reduce((sum, i) => sum + i.stockValue, 0),
    count: stockItems.filter(i => i.category === category).length
  }));

  const totalStockValue = stockItems.reduce((sum, item) => sum + item.stockValue, 0);
  const criticalItems = stockItems.filter(item => item.stockStatus === 'critical').length;
  const lowStockItems = stockItems.filter(item => item.stockStatus === 'low').length;
  const averageStockTurnover = stockItems.reduce((sum, item) => sum + item.stockTurnover, 0) / stockItems.length;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
            Stock Levels Management
          </h1>
          <p className="text-gray-600 mt-2">Real-time inventory monitoring with intelligent alerts and analytics</p>
        </div>
        <div className="flex space-x-3">
          <button className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 flex items-center space-x-2">
            <RefreshCw className="h-4 w-4" />
            <span>Refresh</span>
          </button>
          <button className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 flex items-center space-x-2">
            <Download className="h-4 w-4" />
            <span>Export</span>
          </button>
          <button className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center space-x-2">
            <Plus className="h-4 w-4" />
            <span>Stock Adjustment</span>
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Stock Value"
          value={`$${totalStockValue.toLocaleString()}`}
          subtitle="+5.2% from last month"
          icon={<DollarSign className="h-6 w-6" />}
          trend="vs last month"
          trendValue={5.2}
          color="text-green-600"
          bgGradient="bg-gradient-to-br from-green-500 to-emerald-600"
        />
        <StatCard
          title="Critical Items"
          value={criticalItems}
          subtitle="Requires immediate attention"
          icon={<AlertTriangle className="h-6 w-6" />}
          color="text-red-600"
          bgGradient="bg-gradient-to-br from-red-500 to-red-600"
        />
        <StatCard
          title="Low Stock Items"
          value={lowStockItems}
          subtitle="Below reorder point"
          icon={<Package className="h-6 w-6" />}
          color="text-orange-600"
          bgGradient="bg-gradient-to-br from-orange-500 to-yellow-500"
        />
        <StatCard
          title="Avg Stock Turnover"
          value={`${averageStockTurnover.toFixed(1)}x`}
          subtitle="Annual turnover rate"
          icon={<Activity className="h-6 w-6" />}
          color="text-purple-600"
          bgGradient="bg-gradient-to-br from-purple-500 to-indigo-600"
        />
      </div>

      {/* Stock Alerts */}
      {showAlerts && stockAlerts.length > 0 && (
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <AlertTriangle className="h-6 w-6 text-red-600" />
              <div>
                <h3 className="text-xl font-bold text-gray-900">Stock Alerts ({stockAlerts.length})</h3>
                <p className="text-gray-600 text-sm">Items requiring immediate attention</p>
              </div>
            </div>
            <button 
              onClick={() => setShowAlerts(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <XCircle className="h-5 w-5" />
            </button>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {stockAlerts.slice(0, 6).map((alert) => (
              <div key={alert.id} className={`p-4 rounded-xl border ${
                alert.severity === 'critical' ? 'bg-red-50 border-red-200' : 'bg-orange-50 border-orange-200'
              }`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="font-semibold text-gray-900">{alert.medicationName}</h4>
                    <p className="text-sm text-gray-600">{alert.message}</p>
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    alert.severity === 'critical' ? 'bg-red-100 text-red-800' : 'bg-orange-100 text-orange-800'
                  }`}>
                    {alert.severity.toUpperCase()}
                  </span>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                  <div>
                    <p className="text-gray-600">Current Stock:</p>
                    <p className="font-medium">{alert.currentStock} units</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Days Until Stockout:</p>
                    <p className="font-medium text-red-600">{alert.daysUntilStockout} days</p>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-600">{alert.recommendedAction}</p>
                  <div className="flex space-x-2">
                    <button className="text-purple-600 hover:text-purple-700 text-sm font-medium">
                      Order Now
                    </button>
                    <button className="text-gray-600 hover:text-gray-700 text-sm font-medium">
                      Transfer
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* View Mode Toggle */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex space-x-2">
            <button
              onClick={() => setViewMode('table')}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                viewMode === 'table' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Table View
            </button>
            <button
              onClick={() => setViewMode('cards')}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                viewMode === 'cards' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Card View
            </button>
            <button
              onClick={() => setViewMode('analytics')}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                viewMode === 'analytics' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Analytics
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search medications..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="all">All Categories</option>
            {categories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>

          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="critical">Critical</option>
            <option value="low">Low Stock</option>
            <option value="adequate">Adequate</option>
            <option value="overstocked">Overstocked</option>
          </select>

          <select
            value={selectedSupplier}
            onChange={(e) => setSelectedSupplier(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="all">All Suppliers</option>
            {suppliers.map(supplier => (
              <option key={supplier} value={supplier}>{supplier}</option>
            ))}
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="stockStatus">Stock Status</option>
            <option value="daysOfStock">Days of Stock</option>
            <option value="stockValue">Stock Value</option>
            <option value="name">Name A-Z</option>
          </select>

          <button className="flex items-center justify-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
            <Filter className="h-4 w-4" />
            <span>More Filters</span>
          </button>
        </div>
      </div>

      {/* Analytics View */}
      {viewMode === 'analytics' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Stock Status Distribution */}
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Stock Status Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={stockStatusData}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="count"
                  label={({ status, count }) => `${status}: ${count}`}
                >
                  {stockStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Stock Value by Category */}
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Stock Value by Category</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stockValueData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="category" angle={-45} textAnchor="end" height={80} />
                <YAxis />
                <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, 'Stock Value']} />
                <Bar dataKey="value" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Table View */}
      {viewMode === 'table' && (
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50/50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Medication</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Current Stock</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Reorder Point</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Days of Stock</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Stock Value</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Supplier</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredItems.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50/50">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-900">{item.name}</p>
                        <p className="text-sm text-gray-600">{item.genericName}</p>
                        <p className="text-sm text-gray-500">{item.brandName}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(item.stockStatus)}
                        <span className={`px-3 py-1 text-sm font-medium rounded-full border ${getStatusColor(item.stockStatus)}`}>
                          {item.stockStatus.charAt(0).toUpperCase() + item.stockStatus.slice(1)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-900">{item.currentStock}</p>
                        <div className="w-20 bg-gray-200 rounded-full h-2 mt-1">
                          <div 
                            className={`h-2 rounded-full ${
                              item.stockStatus === 'critical' ? 'bg-red-500' :
                              item.stockStatus === 'low' ? 'bg-orange-500' :
                              item.stockStatus === 'overstocked' ? 'bg-blue-500' : 'bg-green-500'
                            }`}
                            style={{ width: `${Math.min((item.currentStock / item.maximumStock) * 100, 100)}%` }}
                          ></div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-900">{item.reorderPoint}</td>
                    <td className="px-6 py-4">
                      <span className={`font-medium ${
                        item.daysOfStock <= 3 ? 'text-red-600' :
                        item.daysOfStock <= 7 ? 'text-orange-600' : 'text-gray-900'
                      }`}>
                        {item.daysOfStock} days
                      </span>
                    </td>
                    <td className="px-6 py-4 font-medium text-gray-900">
                      ${item.stockValue.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-gray-900">{item.supplier}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg">
                          <Eye className="h-4 w-4" />
                        </button>
                        <button className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg">
                          <Edit className="h-4 w-4" />
                        </button>
                        <button className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg">
                          <ArrowRightLeft className="h-4 w-4" />
                        </button>
                        <button className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg">
                          <ShoppingCart className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Card View */}
      {viewMode === 'cards' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredItems.map((item) => (
            <div key={item.id} className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  {getStatusIcon(item.stockStatus)}
                  <span className={`px-3 py-1 text-sm font-medium rounded-full border ${getStatusColor(item.stockStatus)}`}>
                    {item.stockStatus.charAt(0).toUpperCase() + item.stockStatus.slice(1)}
                  </span>
                </div>
                <div className="flex space-x-1">
                  <button className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg">
                    <Eye className="h-4 w-4" />
                  </button>
                  <button className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg">
                    <Edit className="h-4 w-4" />
                  </button>
                </div>
              </div>
              
              <div className="mb-4">
                <h3 className="font-semibold text-gray-900 mb-1">{item.name}</h3>
                <p className="text-sm text-gray-600">{item.genericName}</p>
                <p className="text-sm text-gray-500">{item.brandName}</p>
              </div>
              
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Current Stock:</span>
                  <span className="text-sm font-medium text-gray-900">{item.currentStock} units</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Days of Stock:</span>
                  <span className={`text-sm font-medium ${
                    item.daysOfStock <= 3 ? 'text-red-600' :
                    item.daysOfStock <= 7 ? 'text-orange-600' : 'text-gray-900'
                  }`}>
                    {item.daysOfStock} days
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Stock Value:</span>
                  <span className="text-sm font-medium text-gray-900">${item.stockValue.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Supplier:</span>
                  <span className="text-sm font-medium text-gray-900">{item.supplier}</span>
                </div>
              </div>
              
              <div className="mt-4">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full ${
                      item.stockStatus === 'critical' ? 'bg-red-500' :
                      item.stockStatus === 'low' ? 'bg-orange-500' :
                      item.stockStatus === 'overstocked' ? 'bg-blue-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${Math.min((item.currentStock / item.maximumStock) * 100, 100)}%` }}
                  ></div>
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>0</span>
                  <span>{item.maximumStock}</span>
                </div>
              </div>
              
              <div className="flex space-x-2 mt-4">
                <button className="flex-1 bg-purple-600 text-white px-3 py-2 rounded-lg hover:bg-purple-700 text-sm font-medium">
                  Order Stock
                </button>
                <button className="flex-1 bg-gray-100 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-200 text-sm font-medium">
                  Transfer
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Results Summary */}
      <div className="flex items-center justify-between text-sm text-gray-600">
        <p>Showing {filteredItems.length} of {stockItems.length} items</p>
        <div className="flex items-center space-x-4">
          <p>Total Value: ${filteredItems.reduce((sum, item) => sum + item.stockValue, 0).toLocaleString()}</p>
          <p>Critical Items: {filteredItems.filter(item => item.stockStatus === 'critical').length}</p>
        </div>
      </div>
    </div>
  );
};

export default StockLevels;
